import React, { useMemo } from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';
import { useGetClassCapacityQuery } from '../../store/api/localApi';

// Order matches the backend's daysOfWeek (0 = Sunday). The picker renders
// only the class's running days, so weekend/non-running entries never show.
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface Props {
  classId: string;
  className: string;
  /** Currently selected days. Empty means nothing selected (intentional default). */
  value: number[];
  onChange: (daysOfWeek: number[]) => void;
  /** Disables the entire picker (e.g. while a parent submission is in flight). */
  disabled?: boolean;
  /**
   * Days the child ALREADY holds a seat on (editing an existing enrolment).
   * The capacity counts include that seat, so on these days the child is not
   * an extra body: the day is never "full" for them, and selecting it does not
   * project one more. Absent for a new enrolment.
   */
  ownDays?: number[];
}

/**
 * Per-class day picker for new enrollments.
 *
 * One large tile per running day. The whole tile is the toggle; selected
 * tiles render in the project's primary blue and report the optimistic
 * (post-enrollment) count so the digit + bar respond to clicks immediately
 * without waiting for an API round-trip.
 *
 * States: default · selected · full (disabled, struck). A selected day is
 * never shown full: it can always be deselected.
 */
const ClassDayPicker: React.FC<Props> = ({
  classId,
  className,
  value,
  onChange,
  disabled,
  ownDays,
}) => {
  const { data, isLoading, isError } = useGetClassCapacityQuery(classId);

  // Per day: how many OTHER children hold a seat, and whether one more fits.
  const seats = useMemo(() => {
    const own = new Set(ownDays ?? []);
    return (data?.perDay || []).map(({ day, enrolled }) => {
      const others = own.has(day) ? Math.max(0, enrolled - 1) : enrolled;
      const remaining = Math.max(0, (data?.capacity ?? 0) - others);
      return { day, others, remaining, isFull: remaining < 1 };
    });
  }, [data, ownDays]);

  const availableDays = useMemo(() => seats.filter((s) => !s.isFull).map((s) => s.day), [seats]);
  const selectedSet = useMemo(() => new Set(value), [value]);

  // Fullness only stops ADDING a day. A selected day can always be dropped:
  // when capacity was lowered below the enrolled count, the child's own day is
  // still over-full after discounting their seat, and an admin must be able to
  // take it away (the backend never checks capacity on dropped days).
  const toggle = (day: number, isFull: boolean) => {
    if (disabled) return;
    if (selectedSet.has(day)) {
      onChange(value.filter((d) => d !== day));
      return;
    }
    if (isFull) return;
    onChange([...value, day].sort((a, b) => a - b));
  };

  // Adds every day with room; never drops a day already selected (a selected
  // day may be over-full, see `toggle`).
  const selectAllAvailable = () =>
    onChange([...new Set([...value, ...availableDays])].sort((a, b) => a - b));
  const clear = () => onChange([]);

  if (isLoading) {
    return (
      <div
        className="rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500"
        data-testid={`day-picker-${classId}-loading`}
      >
        Loading capacity for {className}…
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        Couldn't load capacity for {className}. Try again.
      </div>
    );
  }

  return (
    <div
      data-testid={`day-picker-${classId}`}
      className="rounded-md border border-gray-200 bg-white p-4"
    >
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
        {seats.map(({ day, others, remaining, isFull }) => {
          const isSelected = selectedSet.has(day);
          // Full and not selected: cannot be added. Full but selected stays a
          // live, selected tile so it can be dropped.
          const blocked = isFull && !isSelected;
          const interactive = !blocked && !disabled;

          // Optimistic projection: when a tile is selected we show what the
          // count *would* be after enrollment lands, not the current server
          // state. Capped at capacity so we never project past 100%.
          const projectedEnrolled = isSelected ? Math.min(data.capacity, others + 1) : others;
          const usedPct = Math.min(100, Math.round((projectedEnrolled / data.capacity) * 100));

          let stateClasses: string;
          if (blocked) {
            stateClasses = 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed';
          } else if (isSelected) {
            stateClasses = 'border-primary-600 bg-primary-600 text-white shadow-sm cursor-pointer';
          } else {
            stateClasses =
              'border-gray-200 bg-white text-gray-900 hover:border-primary-400 hover:bg-primary-50 cursor-pointer';
          }

          let trackClass: string;
          let fillClass: string;
          if (blocked) {
            trackClass = 'bg-gray-200';
            fillClass = 'bg-gray-400';
          } else if (isSelected) {
            trackClass = 'bg-white/25';
            fillClass = 'bg-white';
          } else {
            trackClass = 'bg-gray-200';
            fillClass = 'bg-primary-600';
          }

          return (
            <button
              key={day}
              type="button"
              role="checkbox"
              aria-checked={isSelected}
              aria-label={`${DAY_NAMES[day]} — ${
                blocked ? 'full' : `${others} of ${data.capacity} enrolled, ${remaining} remaining`
              }`}
              data-testid={`day-picker-${classId}-day-${day}`}
              disabled={!interactive}
              onClick={() => toggle(day, isFull)}
              className={`relative flex flex-col items-center justify-center rounded-md border px-3 py-3.5 transition-colors ${stateClasses}`}
            >
              {isSelected && (
                <CheckIcon
                  aria-hidden="true"
                  className="absolute top-1.5 right-1.5 h-4 w-4 text-white"
                />
              )}
              <span className="text-base font-semibold leading-none">{DAY_NAMES[day]}</span>
              <div
                aria-hidden="true"
                className={`mt-2 h-[3px] w-full rounded-full overflow-hidden ${trackClass}`}
              >
                <div
                  className={`h-full ${fillClass} transition-[width]`}
                  style={{ width: `${usedPct}%` }}
                />
              </div>
              <span
                className={`mt-1.5 text-[0.7rem] leading-none tabular-nums ${
                  blocked
                    ? 'text-gray-400 line-through'
                    : isSelected
                    ? 'text-primary-100'
                    : 'text-gray-500'
                }`}
              >
                {blocked ? 'Full' : `${projectedEnrolled} / ${data.capacity}`}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between mt-3">
        <span
          className={`text-xs ${value.length === 0 ? 'text-gray-500' : 'text-gray-900'}`}
          data-testid={`day-picker-summary-${classId}`}
        >
          {value.length === 0
            ? 'Pick at least one day'
            : `${value.length} day${value.length === 1 ? '' : 's'} selected`}
        </span>
        <div className="flex items-center gap-3 text-xs">
          {value.length > 0 && (
            <button
              type="button"
              onClick={clear}
              disabled={disabled}
              className="text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
          )}
          <button
            type="button"
            onClick={selectAllAvailable}
            disabled={disabled || availableDays.length === 0}
            data-testid={`day-picker-select-all-${classId}`}
            className="font-medium text-primary-600 hover:text-primary-700 disabled:text-gray-400"
          >
            Select all available
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClassDayPicker;
